/**
 * Políticas de rol compuestas para render condicional en la UI.
 * Usar con `usePermissions().roleKeys` (claves DEPT:Role de getUserRoleKeys).
 */

// --- People Management / Labor ---

/** Roles de Production con acceso completo en People Management (labor). */
export const PROD_LABOR_ELEVATED_ROLE_KEYS = [
  'PROD:Supervisor',
  'PROD:Manager',
  'PROD:Process Engineer',
];

/** Roles base de planta en Production (vista restringida si no hay elevado). */
export const PROD_LABOR_BASE_ROLE_KEYS = [
  'PROD:Operator',
  'PROD:Assistant',
];

/** Roles de RRHH con permisos administrativos en labor. */
export const HR_LABOR_ADMIN_ROLE_KEYS = [
  'HR:Manager',
  'HR:Assistant',
];

/** Roles con gestión de equipos a nivel fábrica (CRUD completo). */
export const PROD_TEAM_MANAGER_ROLE_KEYS = [
  'PROD:Manager',
  'PROD:Process Engineer',
];

// --- Mantenimiento ---

/** Roles de Maintenance con acceso completo (gestión de planes y OT). */
export const MAIN_MAINTENANCE_ELEVATED_ROLE_KEYS = [
  'MAIN:Supervisor',
  'MAIN:Manager',
];

/** Roles base de campo en Maintenance (vista restringida si no hay elevado). */
export const MAIN_MAINTENANCE_BASE_ROLE_KEYS = [
  'MAIN:Technician',
  'MAIN:Electrician',
  'MAIN:Mechanic',
];

/**
 * Tiene al menos un rol base y ningún rol elevado del mismo conjunto.
 *
 * @param {Set<string>} roleKeys
 * @param {readonly string[]} baseKeys
 * @param {readonly string[]} elevatedKeys
 * @returns {boolean}
 */
export function hasBaseWithoutElevated(roleKeys, baseKeys, elevatedKeys) {
  if (!roleKeys || roleKeys.size === 0) return false;
  if (!baseKeys.some((key) => roleKeys.has(key))) return false;
  return !elevatedKeys.some((key) => roleKeys.has(key));
}

/**
 * Usuario con rol base de Production sin roles elevados en Production.
 * Vista solo lectura y datos propios en calendario, asistencia, ausencias y horas.
 *
 * @param {Set<string>} roleKeys
 * @returns {boolean}
 */
export function isProdOperatorOnlyViewer(roleKeys) {
  return hasBaseWithoutElevated(
    roleKeys,
    PROD_LABOR_BASE_ROLE_KEYS,
    PROD_LABOR_ELEVATED_ROLE_KEYS,
  );
}

/**
 * RRHH con acceso administrativo en módulos de labor.
 * Tiene prioridad sobre isProdOperatorOnlyViewer (no queda en modo viewer).
 *
 * @param {Set<string>} roleKeys
 * @returns {boolean}
 */
export function isHrLaborAdmin(roleKeys) {
  if (!roleKeys || roleKeys.size === 0) return false;
  return HR_LABOR_ADMIN_ROLE_KEYS.some((key) => roleKeys.has(key));
}

/**
 * Asignar o revocar habilidades de un empleado.
 * RRHH (HR:Manager, HR:Assistant) y Operational Technology
 * (OT:Manager, OT:System Administrator, OT:Support Technician).
 * Los supervisores no.
 *
 * @param {Set<string>} roleKeys
 * @returns {boolean}
 */
export function canAssignEmployeeSkills(roleKeys) {
  if (!roleKeys || roleKeys.size === 0) return false;
  if (isHrLaborAdmin(roleKeys)) return true;
  return [...roleKeys].some((key) => key.startsWith('OT:'));
}

/**
 * Usuario RRHH acotado a People Management: tiene rol HR y ningún rol fuera de HR.
 * Si además tiene roles de otro departamento (ej. PROD:Manager), no aplica el límite.
 *
 * @param {Set<string>} roleKeys
 * @returns {boolean}
 */
export function isHrLaborOnlyScoped(roleKeys) {
  if (!isHrLaborAdmin(roleKeys)) return false;
  return [...roleKeys].every((key) => key.startsWith('HR:'));
}

/**
 * Vista restringida en People Management: operario/assistant de prod sin elevación.
 * Si además tiene rol de RRHH, gana el acceso administrativo (no es viewer).
 *
 * @param {Set<string>} roleKeys
 * @returns {boolean}
 */
export function isLaborRestrictedViewer(roleKeys) {
  if (isHrLaborAdmin(roleKeys)) return false;
  return isProdOperatorOnlyViewer(roleKeys);
}

/**
 * Puede crear/editar/eliminar equipos y miembros de toda la fábrica.
 *
 * @param {Set<string>} roleKeys
 * @returns {boolean}
 */
export function canManageFactoryTeams(roleKeys) {
  if (isHrLaborAdmin(roleKeys)) return true;
  if (!roleKeys || roleKeys.size === 0) return false;
  return PROD_TEAM_MANAGER_ROLE_KEYS.some((key) => roleKeys.has(key));
}

/**
 * Patrón de asistencia resuelto automáticamente (sin selector manual).
 * Supervisor: patrón del equipo. Operario/assistant: patrón propio.
 *
 * @param {Set<string>} roleKeys
 * @returns {boolean}
 */
export function isLaborAutoPatternScoped(roleKeys) {
  return isProdSupervisorTeamScoped(roleKeys) || isLaborRestrictedViewer(roleKeys);
}

/**
 * Supervisor de Production acotado a su equipo: lectura en equipos,
 * validación de asistencia, aprobación de ausencias y consulta de horas solo de su equipo.
 *
 * @param {Set<string>} roleKeys
 * @returns {boolean}
 */
export function isProdSupervisorTeamScoped(roleKeys) {
  if (!roleKeys?.has('PROD:Supervisor')) return false;
  return !canManageFactoryTeams(roleKeys);
}

/**
 * Técnico de mantenimiento (o electricista/mecánico) sin roles elevados en Maintenance.
 * Para ejecución de OT: sin gestionar planes ni administrar asignaciones.
 *
 * @param {Set<string>} roleKeys
 * @returns {boolean}
 */
export function isMainTechnicianOnlyViewer(roleKeys) {
  return hasBaseWithoutElevated(
    roleKeys,
    MAIN_MAINTENANCE_BASE_ROLE_KEYS,
    MAIN_MAINTENANCE_ELEVATED_ROLE_KEYS,
  );
}

/**
 * OT visible para técnico en modo viewer si está en el equipo o tiene tareas asignadas.
 * Si el listado no trae assignments/tasks, no filtra (comportamiento previo).
 *
 * @param {object | null | undefined} workOrder
 * @param {number | null} employeeId
 * @returns {boolean}
 */
export function isWorkOrderVisibleToMaintenanceViewer(workOrder, employeeId) {
  if (!workOrder || employeeId == null) return false;

  const id = Number(employeeId);
  const assignments = workOrder.assignments;
  const tasks = workOrder.tasks;
  const hasAssignmentData = Array.isArray(assignments);
  const hasTaskData = Array.isArray(tasks);

  if (!hasAssignmentData && !hasTaskData) return true;

  if (hasAssignmentData && assignments.some((a) => Number(a.employee_id) === id)) {
    return true;
  }
  if (hasTaskData && tasks.some((t) => Number(t.assigned_to) === id)) {
    return true;
  }
  return false;
}
